import {
  calculateBearing,
  calculateDistance,
  destinationPoint,
} from './coordinates';
import { PlannerShape, ShapeKind } from '../types/missionPlanner';

/** A [lat, lng] tuple, the order Leaflet expects. */
export type LatLngTuple = [number, number];

/** Number of vertices for regular-polygon kinds. */
const POLY_SIDES: Partial<Record<ShapeKind, number>> = {
  square: 4,
  pentagon: 5,
};

// Start offset (deg) so a "square" reads axis-aligned and a pentagon points up.
const POLY_START: Record<number, number> = {
  4: 45,
  5: -90,
};

// Points used to approximate a circle/ellipse outline.
const ELLIPSE_STEPS = 48;

/** An offset from a shape's centre in metres, in a local east/north frame. */
export interface OffsetM {
  east: number;
  north: number;
}

/**
 * A position in the shape's own frame, in metres from its centre: `major`
 * runs along the shape's rotation bearing, `minor` at right angles to it.
 * Resizing and rotating are simple arithmetic in this frame (#435).
 */
export interface AxisOffsetM {
  major: number;
  minor: number;
}

/**
 * Rotate a shape-frame offset into the east/north frame. Self-inverse: the
 * same transform maps east/north back onto major/minor, which is what
 * {@link toShapeFrame} relies on.
 */
const axisToOffset = (major: number, minor: number, φ: number): OffsetM => ({
  east: major * Math.sin(φ) + minor * Math.cos(φ),
  north: major * Math.cos(φ) - minor * Math.sin(φ),
});

/**
 * Half-extents of the shape along its own axes, in metres. This is where each
 * kind's radius model is interpreted once, so resize maths never has to.
 */
export const shapeHalfExtents = (shape: PlannerShape): AxisOffsetM => {
  const { radiusM, radiusMinorM, kind } = shape;
  switch (kind) {
    // Corners sit at the circumradius, so each half-side is R/√2.
    case 'square':
      return {
        major: radiusM * Math.SQRT1_2,
        minor: radiusMinorM * Math.SQRT1_2,
      };
    case 'ellipse':
      return { major: radiusM, minor: radiusMinorM };
    // Circle and pentagon stay regular — one radius in both directions.
    default:
      return { major: radiusM, minor: radiusM };
  }
};

/** Where a point sits in the shape's own frame, in metres from its centre. */
export const toShapeFrame = (
  shape: PlannerShape,
  lat: number,
  lng: number
): AxisOffsetM => {
  const metres = calculateDistance(shape.center.lat, shape.center.lng, lat, lng) * 1000;
  const bearing = calculateBearing(shape.center.lat, shape.center.lng, lat, lng);
  const θ = (bearing * Math.PI) / 180;
  const east = metres * Math.sin(θ);
  const north = metres * Math.cos(θ);
  const φ = (shape.rotationDeg * Math.PI) / 180;
  // The east/north → major/minor rotation is its own inverse.
  const { east: major, north: minor } = axisToOffset(east, north, φ);
  return { major, minor };
};

/** Project a shape-frame offset back to a [lat, lng] tuple. */
export const fromShapeFrame = (
  shape: PlannerShape,
  { major, minor }: AxisOffsetM
): LatLngTuple =>
  offsetToLatLng(
    shape.center,
    axisToOffset(major, minor, (shape.rotationDeg * Math.PI) / 180)
  );

/**
 * Outline vertices as metre offsets from the shape's centre. This is the frame
 * planar geometry (sweep-line clipping, #347) works in — projecting once here
 * keeps the trigonometry out of every consumer.
 */
export const shapeOffsets = (shape: PlannerShape): OffsetM[] => {
  const { radiusM, radiusMinorM, rotationDeg, kind } = shape;
  const sides = POLY_SIDES[kind];
  const φ = (rotationDeg * Math.PI) / 180;

  // A square is the one polygon whose two axes move independently (#435), so
  // it is built from its half-extents rather than a single circumradius. With
  // equal radii this reproduces the regular square exactly.
  if (kind === 'square') {
    const { major: a, minor: b } = shapeHalfExtents(shape);
    return (
      [
        [a, b],
        [-a, b],
        [-a, -b],
        [a, -b],
      ] as const
    ).map(([major, minor]) => axisToOffset(major, minor, φ));
  }

  if (sides) {
    const start = POLY_START[sides] ?? 0;
    return Array.from({ length: sides }, (_, i) => {
      const bearing = ((rotationDeg + start + (i * 360) / sides) * Math.PI) / 180;
      return {
        east: radiusM * Math.sin(bearing),
        north: radiusM * Math.cos(bearing),
      };
    });
  }

  // circle (a == b) and ellipse: sample the parametric outline.
  const a = radiusM;
  const b = kind === 'ellipse' ? radiusMinorM : radiusM;
  return Array.from({ length: ELLIPSE_STEPS }, (_, i) => {
    const t = (i * 2 * Math.PI) / ELLIPSE_STEPS;
    const u = a * Math.cos(t); // along major axis
    const v = b * Math.sin(t); // along minor axis
    // Rotate (u,v) into east/north offsets, major axis pointing at bearing φ.
    return {
      east: u * Math.sin(φ) + v * Math.cos(φ),
      north: u * Math.cos(φ) - v * Math.sin(φ),
    };
  });
};

/** Project a local metre offset from `center` back to a [lat, lng] tuple. */
export const offsetToLatLng = (
  center: { lat: number; lng: number },
  { east, north }: OffsetM
): LatLngTuple => {
  const p = destinationPoint(
    center.lat,
    center.lng,
    Math.hypot(east, north),
    (Math.atan2(east, north) * 180) / Math.PI
  );
  return [p.lat, p.lng];
};

/**
 * Outline vertices for a shape, in [lat, lng] order, ready for a Leaflet
 * Polygon. Circles are also returned as a polygon approximation so the same
 * code can compute bounds; the map may still render a true Circle for crispness.
 */
export const shapeVertices = (shape: PlannerShape): LatLngTuple[] =>
  shapeOffsets(shape).map((offset) => offsetToLatLng(shape.center, offset));

/** Area of a shape in square metres (planar approximation). */
export const shapeAreaM2 = (shape: PlannerShape): number => {
  const { radiusM, radiusMinorM, kind } = shape;
  switch (kind) {
    case 'circle':
      return Math.PI * radiusM * radiusM;
    case 'ellipse':
      return Math.PI * radiusM * radiusMinorM;
    // (2a)(2b) with half-sides R/√2 — reduces to 2R² when the axes are equal.
    case 'square':
      return 2 * radiusM * radiusMinorM;
    default: {
      // Regular n-gon with circumradius R: (n/2) R² sin(2π/n).
      const n = POLY_SIDES[kind] ?? 4;
      return 0.5 * n * radiusM * radiusM * Math.sin((2 * Math.PI) / n);
    }
  }
};

/** Bounding box [[minLat, minLng], [maxLat, maxLng]] enclosing the shape. */
export const shapeBounds = (shape: PlannerShape): [LatLngTuple, LatLngTuple] => {
  const verts = shapeVertices(shape);
  let minLat = verts[0][0];
  let maxLat = verts[0][0];
  let minLng = verts[0][1];
  let maxLng = verts[0][1];
  for (const [lat, lng] of verts) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
};

/** Format a square-metre area as m² below 1 km², else km² (2 dp). */
export const formatArea = (areaM2: number): string => {
  if (areaM2 < 1_000_000) {
    return `${Math.round(areaM2).toLocaleString()} m²`;
  }
  return `${(areaM2 / 1_000_000).toFixed(2)} km²`;
};

/**
 * Whether rotating a shape kind changes anything. Only a circle is invariant
 * under rotation — everything else, squares included (#435), is rotatable.
 */
export const isRotatable = (kind: ShapeKind): boolean => kind !== 'circle';

/**
 * Whether a kind must stay regular — one radius in both directions. A circle
 * stays a circle and a pentagon stays equilateral however its handles are
 * dragged; only squares (into rectangles) and ellipses resize per axis.
 */
export const isUniform = (kind: ShapeKind): boolean =>
  kind === 'circle' || kind === 'pentagon';

/** Smallest half-extent a shape can be resized to, in metres. */
export const MIN_SHAPE_RADIUS_M = 5;

/**
 * Which corner or edge a resize handle sits on, as signs in the shape's own
 * frame: ±1 on an axis means that end, 0 means centred on it. A corner has
 * both non-zero, an edge exactly one.
 */
export interface ShapeHandleAxis {
  major: -1 | 0 | 1;
  minor: -1 | 0 | 1;
}

/** The eight resize handles, corners first. */
export const RESIZE_HANDLES: ReadonlyArray<ShapeHandleAxis> = [
  { major: 1, minor: 1 },
  { major: 1, minor: -1 },
  { major: -1, minor: -1 },
  { major: -1, minor: 1 },
  { major: 1, minor: 0 },
  { major: -1, minor: 0 },
  { major: 0, minor: 1 },
  { major: 0, minor: -1 },
];

/** Where a resize handle rests on the shape's bounding box. */
export const handlePosition = (
  shape: PlannerShape,
  handle: ShapeHandleAxis
): LatLngTuple => {
  const extents = shapeHalfExtents(shape);
  return fromShapeFrame(shape, {
    major: handle.major * extents.major,
    minor: handle.minor * extents.minor,
  });
};

/**
 * The four corners of the shape's own editing frame, in cyclic order (#442).
 *
 * Built from the corner handles rather than alongside them, so the dashed frame
 * drawn around a shape is by construction the thing its handles sit on. Drawing
 * the two separately is what broke: handles were placed via `fromShapeFrame`, in
 * the shape's *rotated* frame, while the frame itself was `shapeBounds` — the
 * world-aligned bounding box. At 0° the two coincide, which is why it looked
 * right when the handles landed (#435); at 40° every corner handle sits outside
 * the box it is supposed to define.
 *
 * `shapeBounds` is unchanged and still correct for fitting the map to a shape.
 * It is simply not the frame an operator is editing in.
 */
export const shapeFrameCorners = (shape: PlannerShape): LatLngTuple[] =>
  RESIZE_HANDLES.filter(
    (handle) => handle.major !== 0 && handle.minor !== 0
  ).map((handle) => handlePosition(shape, handle));

/** The radius fields a resize produces, plus the centre it resizes about. */
export interface ShapeResize {
  center: { lat: number; lng: number };
  radiusM: number;
  radiusMinorM: number;
}

/**
 * Resize a shape by dragging one of its handles to (`lat`, `lng`), keeping the
 * opposite corner or edge anchored (#435) — so the shape grows from the side
 * being pulled rather than ballooning about its centre.
 *
 * A corner drives both axes; an edge drives only its own, leaving the other
 * extent and the centre's position on that axis untouched. Uniform kinds
 * (circle, pentagon) average the axes back together so they stay regular,
 * while still honouring the anchor.
 */
export const resizeShapeTo = (
  shape: PlannerShape,
  handle: ShapeHandleAxis,
  lat: number,
  lng: number
): ShapeResize => {
  const extents = shapeHalfExtents(shape);
  // A square's handles sit on its sides, so its floor is a half-side too.
  const minExtent =
    shape.kind === 'square'
      ? MIN_SHAPE_RADIUS_M * Math.SQRT1_2
      : MIN_SHAPE_RADIUS_M;
  const drop = toShapeFrame(shape, lat, lng);

  // Distance from the anchored edge to the drop point, halved: the new
  // half-extent. Axes the handle does not drive keep their current extent.
  const resized = (axis: keyof AxisOffsetM): number =>
    handle[axis] === 0
      ? extents[axis]
      : Math.max(minExtent, Math.abs(drop[axis] + handle[axis] * extents[axis]) / 2);

  let major = resized('major');
  let minor = resized('minor');

  if (isUniform(shape.kind)) {
    // Corner drags average both axes; an edge drag is driven by its own axis.
    const uniform =
      handle.major !== 0 && handle.minor !== 0
        ? (major + minor) / 2
        : handle.major !== 0
          ? major
          : minor;
    major = uniform;
    minor = uniform;
  }

  // Moving the far side of the shape means the centre moves with it.
  const center = fromShapeFrame(shape, {
    major: handle.major * (major - extents.major),
    minor: handle.minor * (minor - extents.minor),
  });

  const toRadius = (extent: number): number =>
    shape.kind === 'square' ? extent * Math.SQRT2 : extent;

  return {
    center: { lat: center[0], lng: center[1] },
    radiusM: toRadius(major),
    radiusMinorM: toRadius(minor),
  };
};
